// External Imports
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { requestAdminAPI } from "../../api/posh-api";
import { UtilityContext } from "../../context/util-context"; 
import { MdSearch } from "react-icons/md";
import LoadingSpinner from "../UI/LoadingSpinner";

// Internal Imports

/** ========================================================================
 * * VehicleSelector Component
 * Renders a dropdown search component, which allows selecting an option from a list.
 * ========================================================================== */

export default function VehicleSelector({ onSelect, selected, label = "VEHICLE" }) {
  // Initialize States
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [show, setShow] = useState(false);

  /* ======================= FETCH VEHICLES ======================= */
  const fetchRecords = async (e) => {
    e.preventDefault();
    const query = e.target.query.value?.toLowerCase();
    if (!query) return;
    try {
      setLoading(true);
      setShow(false);
      const res = await requestAdminAPI("search", {
        table: "vehicle",
        filters: { query },
      });
      setVehicles(res);
      setShow(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= FETCH VEHICLE ======================= */
  const fetchVehicle = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch", {
        table: "vehicle",
        id: selected,
        columns: ["make { name }", "plan { order }"],
      });
      optionClickHandler(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Declare Refs
  const inputRef = useRef(null);
  const valueRef = useRef(null);

  /* ======================= NAME FORMATTER ======================= */
  const nameFormatter = (vehicle) => {
    if (!vehicle) return "";
    return `${vehicle.year} ${vehicle.make.name} ${vehicle.model} ${
      vehicle.color
    } (${
      vehicle.license
    }) - ${vehicle.vin.substring(13, 17)}`;
  };

  /* ======================= OPTION CLICK HANDLER ======================= */
  const optionClickHandler = (vehicle) => {
    inputRef.current.value = nameFormatter(vehicle);
    valueRef.current.value = vehicle.id;
    setShow(false);
    onSelect(vehicle);
  };

  // If selection is made outside of the dropdown
  useEffect(() => {
    if (selected && valueRef.current.value !== selected) fetchVehicle();
  }, [selected]);

  return (
    <Fragment>
      {show && (
        <div
          onClick={() => setShow(false)}
          className="absolute top-0 left-0 w-full h-full -z-1 no-scrollbar"
        ></div>
      )}
      <div className="relative grid gap-2">
        <form onSubmit={fetchRecords} className="">
          <label className="text-sm text-stone-400">{label}:</label>
          <div className="flex items-center border rounded-md border-stone-600 ">
            <input
              className="relative w-full input"
              autoComplete="off"
              name="query"
              type="text"
              ref={inputRef}
              autoCorrect="off"
              spellCheck="false"
              placeholder="Type here and press enter to search..."
            />
            <button type="submit" className="px-2">
              {loading ? (
                <LoadingSpinner classes="text-primary w-6 h-6" />
              ) : (
                <MdSearch className="icon" />
              )}
            </button>
          </div>
        </form>
        <input className="hidden" name="vehicle" id="vehicle" ref={valueRef} />
        {show && (
          <div className="absolute top-16 py-2 px-4 w-full overflow-scroll rounded-lg bg-stone-700 no-scrollbar max-h-[300px] z-10">
            {vehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                className="px-2 py-1 text-sm font-semibold rounded-md cursor-pointer text-stone-200 hover:bg-stone-600"
                onClick={() => optionClickHandler(vehicle)}
              >
                {nameFormatter(vehicle)}
              </div>
            ))}
          </div>
        )}
      </div>
    </Fragment>
  );
}
